/** A whole string that is a bare calendar date, with no time component at all. */
const DATE_ONLY = /^\d{4}-\d{2}-\d{2}$/;

/**
 * Parse an ISO string for the two DATE formatters below.
 *
 * `new Date("2026-01-15")` is specified to parse as UTC midnight, so rendering
 * it with `toLocaleDateString` shows the PREVIOUS day to every viewer west of
 * UTC — the renewal column reads a day early for exactly the clinicians this
 * product is sold to. Appending `T00:00:00` makes the same string parse as
 * LOCAL midnight, which is the day the string actually names.
 *
 * The test is on the SHAPE of the whole string, so anything carrying a time
 * component (including the `${iso}T00:00:00` two call sites already build for
 * themselves) still takes the untouched instant path.
 *
 * Malformed input behaves EXACTLY as it did before this helper existed — both
 * branches hand the same two classes to the same `new Date`, so the callers'
 * placeholder guard catches whatever it caught before. Note the two classes are
 * not the same: an out-of-RANGE component ("2026-13-45", "0000-00-00") yields
 * an Invalid Date, while an in-range day that does not exist ("2026-02-30")
 * rolls over to Mar 2 and always did. Only the ZONE interpretation changes here.
 *
 * Deliberately NOT used by `formatDateTime`, which renders an instant rather
 * than a day — see its docstring for why that distinction is load-bearing.
 */
function parseIsoDateOrInstant(iso: string): Date {
  return new Date(DATE_ONLY.test(iso) ? `${iso}T00:00:00` : iso);
}

// Locale short date (e.g. "Jun 20, 2026"). Returns the em-dash placeholder for a
// null/blank/invalid input so callers can render it directly. Shared by the admin
// legacy-cert surfaces (queue + review panel) — extracted to keep the two formatters
// from drifting.
export function formatDate(iso: string | null | undefined): string {
  if (!iso) return "—";
  const d = parseIsoDateOrInstant(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

/**
 * Format an ISO timestamp as a compact, locale-aware date (e.g. "Apr 20, 2026"), or
 * "—" when absent/unparseable. Shared by the clients/evaluations lists + workspace home
 * so the Date columns render identically. Pair with `tabular-nums` at the call-site.
 */
export function formatShortDate(iso: string | null | undefined): string {
  if (!iso) return "—";
  const date = parseIsoDateOrInstant(iso);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
}

/**
 * Format an ISO instant as a local date + time TO THE SECOND with an explicit zone
 * (e.g. "Jun 1, 2026, 12:00:00 PM GMT+2"), or "—" when absent/unparseable.
 *
 * Story 11.7: the audit console is a forensic surface, so the seconds and the zone
 * are both load-bearing — two events one second apart are two different events, and
 * a bare local time an operator cannot place in a zone is not evidence. Pair with
 * `tabular-nums` at the call-site.
 *
 * This is why it does NOT go through `parseIsoDateOrInstant` (Story 15.0a,
 * DW-216): that helper re-interprets a date-only string as LOCAL midnight,
 * which is right for a day and wrong for an instant. Every input here carries a
 * time component already, and the AC4 pin exists to keep it that way.
 */
export function formatDateTime(iso: string | null | undefined): string {
  if (!iso) return "—";
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    timeZoneName: "short",
  });
}
